<script lang="ts">
  import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '@banger/shared';
  import HowToPlayLink from '../shared/HowToPlayLink.svelte';
  import BigScreenNote from '../shared/BigScreenNote.svelte';
  import { PARTY_EMOJI as EMOJI } from '../lib/emoji';
  import { joinErrorCopy } from '../lib/joinErrors';
  import { lastError } from '../lib/connection';

  let { code = '', initialName = '', initialEmoji = '', busy = false, note = '', onjoin }: {
    code?: string; initialName?: string; initialEmoji?: string; busy?: boolean;
    /** A gentle message shown instead of the error (e.g. the remembered game ended). */
    note?: string;
    onjoin: (code: string, name: string, emoji: string) => void;
  } = $props();

  // svelte-ignore state_referenced_locally
  let roomCode = $state(code.toUpperCase());
  // svelte-ignore state_referenced_locally
  let name = $state(initialName);
  // svelte-ignore state_referenced_locally
  let emoji = $state(initialEmoji || EMOJI[0]);

  function onCode(e: Event) {
    const el = e.currentTarget as HTMLInputElement;
    roomCode = [...el.value.toUpperCase()].filter((c) => ROOM_CODE_ALPHABET.includes(c)).join('').slice(0, ROOM_CODE_LENGTH);
    el.value = roomCode;
  }
  const ready = $derived(roomCode.length === ROOM_CODE_LENGTH && name.trim().length > 0);
  function submit(e: Event) {
    e.preventDefault();
    if (ready && !busy) onjoin(roomCode, name.trim(), emoji);
  }
</script>

<main>
  <div class="hero"><span class="disco-ball"></span><h1 class="wordmark">BANGER!</h1></div>
  <form onsubmit={submit}>
    <label>Room code
      <input class="code" value={roomCode} oninput={onCode} maxlength="4" placeholder="ABCD"
        autocapitalize="characters" autocomplete="off" autocorrect="off" spellcheck="false" inputmode="text" />
    </label>
    <label>Your name
      <input bind:value={name} maxlength="16" placeholder="Name" autocomplete="off" />
    </label>
    <div class="pick" role="group" aria-label="Pick your emoji">
      {#each EMOJI as e}
        <button type="button" class="em" class:on={emoji === e} aria-pressed={emoji === e} onclick={() => (emoji = e)}>{e}</button>
      {/each}
    </div>
    {#if note}<p class="note" role="status">{note}</p>
    {:else if $lastError}<p class="err">{joinErrorCopy($lastError.code)}</p>{/if}
    <button class="btn-neon go" type="submit" disabled={!ready || busy}>{busy ? 'Joining…' : 'Join'}</button>
  </form>
  <p class="howto"><HowToPlayLink /></p>
  <BigScreenNote />
</main>

<style>
  main { min-height: 100%; padding: 20px 16px 8px; max-width: 480px; margin: 0 auto; }
  .hero { display: flex; align-items: center; justify-content: center; gap: 12px; font-size: 40px; margin-bottom: 16px; }
  h1 { margin: 0; font-size: 2.4rem; }
  form { display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
  label { display: grid; gap: 6px; font-weight: 700; color: var(--cyan); }
  input { width: 100%; min-width: 0;
    font: inherit; font-size: 1.25rem; color: #fff; height: 56px; padding: 0 16px; border-radius: 14px;
    background: rgba(255, 255, 255, 0.08); border: 2px solid rgba(125, 249, 255, 0.4); outline: none;
  }
  input:focus { border-color: var(--cyan); box-shadow: 0 0 14px rgba(125, 249, 255, 0.5); }
  .code { text-transform: uppercase; letter-spacing: 0.4em; text-align: center; font-size: 1.8rem; font-weight: 900; }
  .pick { display: grid; width: 100%; grid-template-columns: repeat(auto-fill, minmax(48px, 1fr)); gap: 8px; }
  .em { font-size: 1.7rem; height: 52px; min-height: 48px; border-radius: 12px; border: 2px solid transparent; background: rgba(255, 255, 255, 0.08); cursor: pointer; }
  .em.on { border-color: var(--pink); background: rgba(255, 61, 242, 0.28); box-shadow: 0 0 12px rgba(255, 61, 242, 0.6); }
  .note { margin: 0; color: var(--cyan); font-weight: 700; text-align: center; }
  .err { margin: 0; color: #ff8fa3; font-weight: 700; text-align: center; }
  .howto { text-align: center; margin: 18px 0 0; }
  .go { height: 60px; font-size: 1.4rem; }
</style>
